import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TablePagination } from '@mui/material';
import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import alertImage from 'assets/img/alert.svg';

import {
  CURRENCY_FORMATTER,
  PAGE_INITIAL_VALUE,
  PRODUCT_INVALID_VALUE,
  PRODUCT_ROWS_PER_PAGE_OPTION,
} from 'common/constants';
import {
  ACTIVE_PRODUCT_MESSAGE,
  DEACTIVE_PRODUCT_MESSAGE,
} from 'common/constants/changeProductAvailabilityMessage.constants';
import { formatCurrency } from 'common/constants/currency';
import { GO_BACK } from 'v3/routes/enum';
import { theme } from 'v3/styles/global-styles';

import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setLimit, setPage, setProducts, setProductSelected, setProductValues } from 'v3/data/store/product/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';

import { IProduct } from 'v3/data/store/product/types';
import { changeProductPrice } from 'v3/pages/Products/List/useCases/change-product-price';
import { getProducts, updateProduct } from 'v3/services/api';

import { SmartphoneFilter } from 'v3/shared/components/Filters/SmartphoneFilter';
import { ChangeValueModal } from 'v3/shared/components/Modal/ChangePriceModal';
import { ConfirmationModal } from 'v3/shared/components/Modal/ConfirmationModal';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { SwitchCommon } from 'v3/shared/components/Switch';
import { PaperStyled } from 'v3/shared/components/Table/styles';
import { CommonTemplate } from 'v3/shared/templates/CommonTemplate';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';

import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { ProductMobileTableForStore } from 'v3/shared/components/MobileTable/ProductMobileTableForStore';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { LabelSmall } from 'v3/shared/typography/LabelSmall';
import {
  Container,
  Content,
  DeviceImg,
  Header,
  SelectPropsStyles,
  TableCellGroup,
  TableCellInColumn,
  TableCellInRow,
  TableCellStyled,
  TableContainerStyled,
  TablePaginationStyles,
  TableRowStyled,
  UnsupportedValueCard,
} from '../styles';

export const StoreProductsListPage = () => {
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoints();

  const { limit, page, available, model } = useStoreSelector((store) => store.products.productsFilter);
  const { productSelected } = useStoreSelector((store) => store.products);
  const { count } = useStoreSelector((store) => store.products.products);
  const { products } = useStoreSelector((store) => store.products);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [changePriceModalOpen, setChangePriceModalOpen] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [sellerFeatureEnabled, setSellerFeatureEnabled] = useState<boolean>(false);

  const fetchSellerFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_3074_IMPLEMENT_MAXIMUM_VALUES);
    if (response.data.enabled) {
      setSellerFeatureEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);

  const fetchProductList = useCallback(async () => {
    try {
      const response = await getProducts({ page, limit, available, model });
      setProducts(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de produtos', error);
    }
  }, [page, limit, available, model]);

  const handleSwitchChange = (product: IProduct, event: React.ChangeEvent<HTMLInputElement>) => {
    const switchNewValue = Boolean(event.target.checked);
    setProductSelected({ ...product, available: switchNewValue });
    const currentSubtitle = switchNewValue ? ACTIVE_PRODUCT_MESSAGE : DEACTIVE_PRODUCT_MESSAGE;
    setSubtitle(currentSubtitle);
    setConfirmationModalOpen(true);
  };
  const changePriceHandler = async (storePrice: number) => {
    await changeProductPrice(productSelected.id, Number(storePrice) / CURRENCY_FORMATTER, productSelected.available);
  };
  const handleOpenEditPriceModal = (product: IProduct) => {
    setProductSelected(product);
    setChangePriceModalOpen(true);
  };

  const handleConfirm = async () => {
    try {
      const response = await updateProduct(productSelected.id, { available: productSelected.available });
      setProductValues(response.data);

      response.data.available
        ? showSnackbar('O produto está ativo no catálogo', 'success', true)
        : showSnackbar('O produto foi desativado do catálogo', 'success', true);
    } catch (error) {
      showErrorDialog('Erro ao editar disponibilidade do produto.', error);
    } finally {
      setConfirmationModalOpen(false);
    }
  };

  const shouldDisableAvailabilityButton = (product: IProduct) => {
    const isUnavailableWithInvalidPrice = !product.available && product.storePrice <= PRODUCT_INVALID_VALUE;
    const isPriceExceedsMaxLimit = product.storePrice > product.sku.maxPrice;
    return isUnavailableWithInvalidPrice || isPriceExceedsMaxLimit;
  };

  useEffect(() => {
    fetchProductList();
  }, [page, limit, available, model]);

  return (
    <CommonTemplate>
      <Container>
        <Content>
          <Header>
            <ArrowBackIcon className="pointer" fontSize="large" onClick={() => navigate(GO_BACK)} />
            <HeadlineMedium>Lista de produtos</HeadlineMedium>
          </Header>
          <SmartphoneFilter />
          {isDesktop ? (
            <PaperStyled>
              <TableContainerStyled>
                {products.items.isEmpty ? (
                  <NoItemsFound />
                ) : (
                  <TableMui stickyHeader aria-label="sticky tables">
                    <TableHead>
                      <TableRow>
                        <TableCellStyled>Modelo</TableCellStyled>
                        <TableCellStyled>Preço</TableCellStyled>
                        <TableCellStyled align="center">Mostrar no catálogo</TableCellStyled>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products?.items?.map((product: IProduct) => (
                        <TableRowStyled key={product.id}>
                          <TableCellStyled sx={{ backgroundColor: 'transparent' }}>
                            <TableCellInRow>
                              <DeviceImg src={product.sku.image.marketingUrl} alt={product.sku.product.name} />
                              <TableCellInColumn>
                                <SubtitleMedium>{product.sku.product.name}</SubtitleMedium>
                                <SubtitleMedium color={theme.palette.secondary.main}>
                                  {` ${product.sku.storage}GB`}
                                </SubtitleMedium>
                              </TableCellInColumn>
                            </TableCellInRow>
                          </TableCellStyled>

                          <TableCellGroup>
                            <TableCellInRow>
                              <SubtitleMedium>{formatCurrency(product.storePrice)}</SubtitleMedium>
                              <EditOutlinedIcon onClick={() => handleOpenEditPriceModal(product)} />
                            </TableCellInRow>
                            {product.storePrice <= PRODUCT_INVALID_VALUE && (
                              <UnsupportedValueCard>
                                <LabelSmall>Necessário informar o preço</LabelSmall>
                              </UnsupportedValueCard>
                            )}
                            {sellerFeatureEnabled && product.storePrice > product.sku.maxPrice && (
                              <UnsupportedValueCard>
                                <LabelSmall>
                                  Valor máximo do produto deve ser{' '}
                                  {formatCurrency(parseFloat(product.sku.maxPrice.toFixed(2)))}
                                </LabelSmall>
                              </UnsupportedValueCard>
                            )}
                          </TableCellGroup>

                          <TableCellGroup align="center">
                            <SwitchCommon
                              disabled={sellerFeatureEnabled ? shouldDisableAvailabilityButton(product) : false}
                              checked={product.available}
                              onChange={(event) => {
                                handleSwitchChange(product, event);
                              }}
                            />
                          </TableCellGroup>
                        </TableRowStyled>
                      ))}
                    </TableBody>
                  </TableMui>
                )}
              </TableContainerStyled>
              <TablePagination
                rowsPerPageOptions={PRODUCT_ROWS_PER_PAGE_OPTION}
                component="div"
                sx={TablePaginationStyles}
                slotProps={{
                  select: {
                    ...SelectPropsStyles,
                    IconComponent: KeyboardArrowDownIcon,
                  },
                }}
                count={count}
                rowsPerPage={limit}
                page={page}
                onPageChange={(event, value) => {
                  setPage(value);
                }}
                onRowsPerPageChange={(event) => {
                  setPage(PAGE_INITIAL_VALUE);
                  setLimit(Number(event.target.value));
                }}
              />
            </PaperStyled>
          ) : (
            <ProductMobileTableForStore
              shouldDisableAvailability={shouldDisableAvailabilityButton}
              items={products.items}
            ></ProductMobileTableForStore>
          )}

          <ChangeValueModal
            modal={{
              initialPrice: productSelected.storePrice,
              active: changePriceModalOpen,
              title: 'Editar Preço',
              activeButton: true,
              cancelButton: 'Cancelar',
              saveButton: 'Salvar',
              maxPrice: productSelected.sku.maxPrice,
            }}
            onClose={() => setChangePriceModalOpen(false)}
            onConfirm={changePriceHandler}
          />

          <ConfirmationModal
            modal={{
              active: confirmationModalOpen,
              title: 'Você tem certeza?',
              subtitle: subtitle,
              image: alertImage,
              cancelButtonText: 'Cancelar',
              confirmButtonText: 'Sim, desejo continuar',
            }}
            onClose={() => setConfirmationModalOpen(false)}
            onConfirm={handleConfirm}
          />
        </Content>
      </Container>
    </CommonTemplate>
  );
};
